import { EntityPropertyChanges, EntityPropertyTypes } from "../../enums";
import { EntityProperty, IAuditLogNormalized } from "../../models";
import {
  AUDIT_LOG_MAX_DELTA_FRACTION_DIGITS,
  AUDIT_LOG_NUMBER_LIST_SEPARATOR,
  AUDIT_LOG_PERCENT_FACTOR,
  AuditLogChangeFilters,
  TAuditLogChangeFilter,
} from "./audit-log.constants";
import type { IAuditLogNumericDelta } from "./audit-log.models";

const roundDelta = (value: number): number =>
  Number(value.toFixed(AUDIT_LOG_MAX_DELTA_FRACTION_DIGITS));

const parseNumberList = (text: string | undefined): number[] | undefined => {
  if (!text) {
    return undefined;
  }
  const numbers = text
    .split(AUDIT_LOG_NUMBER_LIST_SEPARATOR)
    .map(part => Number(part.trim()));
  return numbers.every(number => Number.isFinite(number)) ? numbers : undefined;
};

/** How much numeric values grew or shrank; undefined when either side is not a list of equally many numbers. */
export const getAuditLogNumericDelta = ({
  oldValue,
  value,
}: {
  oldValue?: string;
  value?: string;
}): IAuditLogNumericDelta | undefined => {
  const before = parseNumberList(oldValue);
  const after = parseNumberList(value);
  if (before?.length !== after?.length || !before || !after) {
    return undefined;
  }

  const deltas = after.map((number, index) =>
    roundDelta(number - before[index]),
  );
  const oldTotal = before.reduce((sum, number) => sum + number, 0);
  const newTotal = after.reduce((sum, number) => sum + number, 0);
  const total = roundDelta(newTotal - oldTotal);

  return {
    deltas,
    total,
    percent: oldTotal
      ? roundDelta(
          ((newTotal - oldTotal) / Math.abs(oldTotal)) *
            AUDIT_LOG_PERCENT_FACTOR,
        )
      : undefined,
  };
};

/** `+288.21`, `−64`, `0`: the sign is always written so growth and shrinkage read the same way at a glance. */
export const formatAuditLogDelta = (delta: number): string => {
  if (delta === 0) {
    return "0";
  }
  return delta > 0 ? `+${delta}` : `−${Math.abs(delta)}`;
};

export const getAuditLogDeltaClassName = (delta: number): string => {
  if (delta === 0) {
    return "entityProperty__delta-item";
  }
  return `entityProperty__delta-item entityProperty__delta-item--${delta > 0 ? "up" : "down"}`;
};

/**
 * Items of an ordered list that were moved. A kept item counts as moved unless it belongs to the longest run of kept
 * items that stayed in relative order, so dragging one station reports one station, not everything it jumped over.
 */
export const getAuditLogMovedItems = ({
  before,
  after,
}: {
  before: string[];
  after: string[];
}): Set<string> => {
  const afterSet = new Set(after);
  const beforeIndexById = new Map(before.map((id, index) => [id, index]));
  const kept = after.filter(id => beforeIndexById.has(id));
  const positions = kept.map(id => beforeIndexById.get(id) as number);

  // Longest increasing subsequence of the old positions, taken in the new order.
  const lengthEndingAt: number[] = [];
  const previousIndex: number[] = [];
  let bestEnd = -1;
  positions.forEach((position, index) => {
    lengthEndingAt[index] = 1;
    previousIndex[index] = -1;
    for (let prior = 0; prior < index; prior++) {
      if (
        positions[prior] < position &&
        lengthEndingAt[prior] + 1 > lengthEndingAt[index]
      ) {
        lengthEndingAt[index] = lengthEndingAt[prior] + 1;
        previousIndex[index] = prior;
      }
    }
    if (bestEnd === -1 || lengthEndingAt[index] > lengthEndingAt[bestEnd]) {
      bestEnd = index;
    }
  });

  const stayed = new Set<string>();
  for (let cursor = bestEnd; cursor !== -1; cursor = previousIndex[cursor]) {
    stayed.add(kept[cursor]);
  }

  return new Set(kept.filter(id => afterSet.has(id) && !stayed.has(id)));
};

/** The texts a search looks in: for a changed list only the items that came, went or moved, not the unchanged rest. */
export const getEntityPropertySearchTexts = (
  entityProperty: EntityProperty,
): string[] => {
  if (
    entityProperty.type !== EntityPropertyTypes.Primitive &&
    entityProperty.type !== EntityPropertyTypes.ArrayPrimitive
  ) {
    return [];
  }
  const { items } = entityProperty;
  if (
    entityProperty.changeType === EntityPropertyChanges.ItemsChanged &&
    items
  ) {
    return [...items.added, ...items.removed, ...(items.orderAfter ?? [])];
  }
  return [String(entityProperty.oldValue), String(entityProperty.value)];
};

const matchesChangeFilter = ({
  entityProperty,
  filters,
}: {
  entityProperty: EntityProperty;
  filters: TAuditLogChangeFilter[];
}): boolean => {
  if (
    entityProperty.type !== EntityPropertyTypes.Primitive &&
    entityProperty.type !== EntityPropertyTypes.ArrayPrimitive
  ) {
    return false;
  }
  const { changeType, items } = entityProperty;

  return filters.some(filter => {
    switch (filter) {
      case AuditLogChangeFilters.added:
        return (
          changeType === EntityPropertyChanges.Added || !!items?.added.length
        );
      case AuditLogChangeFilters.removed:
        return (
          changeType === EntityPropertyChanges.Removed ||
          !!items?.removed.length
        );
      case AuditLogChangeFilters.changed:
        return changeType === EntityPropertyChanges.Changed;
      case AuditLogChangeFilters.reordered:
        return !!items?.orderBefore;
    }
  });
};

const filterPropertiesByChange = ({
  entityProperties,
  filters,
}: {
  entityProperties: EntityProperty[];
  filters: TAuditLogChangeFilter[];
}): EntityProperty[] =>
  entityProperties.flatMap((entityProperty): EntityProperty[] => {
    if (entityProperty.type === EntityPropertyTypes.Object) {
      const properties = filterPropertiesByChange({
        entityProperties: entityProperty.properties,
        filters,
      });
      return properties.length ? [{ ...entityProperty, properties }] : [];
    }
    if (entityProperty.type === EntityPropertyTypes.ArrayObject) {
      const rows = entityProperty.properties.map(row => ({
        row,
        kept: filterPropertiesByChange({ entityProperties: row, filters }),
      }));
      const keptRows = rows.filter(({ kept }) => kept.length);
      return keptRows.length
        ? [
            {
              ...entityProperty,
              properties: keptRows.map(({ kept }) => kept),
              itemLabels: entityProperty.itemLabels
                ? rows.flatMap(({ kept }, index) =>
                    kept.length
                      ? [entityProperty.itemLabels?.[index] ?? ""]
                      : [],
                  )
                : undefined,
            },
          ]
        : [];
    }
    return matchesChangeFilter({ entityProperty, filters })
      ? [entityProperty]
      : [];
  });

const filterPropertiesByKey = ({
  entityProperties,
  keySearch,
  getKeyLabel,
}: {
  entityProperties: EntityProperty[];
  keySearch: string;
  getKeyLabel?: (key: string) => string;
}): EntityProperty[] =>
  entityProperties.flatMap((entityProperty): EntityProperty[] => {
    // A field whose own name, or the label shown for it, matches is kept whole with everything nested in it.
    const isKeyMatch = [
      entityProperty.key,
      getKeyLabel?.(entityProperty.key),
    ].some(text => text?.toLowerCase().includes(keySearch));
    if (isKeyMatch) {
      return [entityProperty];
    }
    if (entityProperty.type === EntityPropertyTypes.Object) {
      const properties = filterPropertiesByKey({
        entityProperties: entityProperty.properties,
        keySearch,
        getKeyLabel,
      });
      return properties.length ? [{ ...entityProperty, properties }] : [];
    }
    if (entityProperty.type === EntityPropertyTypes.ArrayObject) {
      const rows = entityProperty.properties.flatMap((row, index) => {
        const properties = filterPropertiesByKey({
          entityProperties: row,
          keySearch,
          getKeyLabel,
        });
        return properties.length
          ? [{ properties, itemLabel: entityProperty.itemLabels?.[index] }]
          : [];
      });
      return rows.length
        ? [
            {
              ...entityProperty,
              properties: rows.map(row => row.properties),
              itemLabels: entityProperty.itemLabels
                ? rows.map(row => row.itemLabel ?? "")
                : undefined,
            },
          ]
        : [];
    }
    return [];
  });

/**
 * Keeps only the fields whose name contains `keySearch` (any depth, any case); rows left with nothing are dropped.
 * `getKeyLabel` adds the label shown for a field, so a user can search by what they read on screen.
 */
export const filterAuditLogsByKey = ({
  auditLogs,
  keySearch,
  getKeyLabel,
}: {
  auditLogs: IAuditLogNormalized[];
  keySearch: string;
  getKeyLabel?: (key: string) => string;
}): IAuditLogNormalized[] => {
  const keySearchLower = keySearch.trim().toLowerCase();
  if (!keySearchLower) {
    return auditLogs;
  }

  return auditLogs.flatMap(auditLog => {
    const entityProperties = filterPropertiesByKey({
      entityProperties: auditLog.entityProperties ?? [],
      keySearch: keySearchLower,
      getKeyLabel,
    });
    return entityProperties.length ? [{ ...auditLog, entityProperties }] : [];
  });
};

/** Keeps only the fields that match one of the chosen kinds of change; rows left with nothing are dropped. */
export const filterAuditLogsByChangeType = ({
  auditLogs,
  filters,
}: {
  auditLogs: IAuditLogNormalized[];
  filters: TAuditLogChangeFilter[];
}): IAuditLogNormalized[] => {
  if (!filters.length) {
    return auditLogs;
  }

  return auditLogs.flatMap(auditLog => {
    const entityProperties = filterPropertiesByChange({
      entityProperties: auditLog.entityProperties ?? [],
      filters,
    });
    return entityProperties.length ? [{ ...auditLog, entityProperties }] : [];
  });
};

/** How many records each kind of change would leave, so a chip can say what pressing it shows. */
export const getAuditLogChangeFilterCounts = (
  auditLogs: IAuditLogNormalized[],
): Record<TAuditLogChangeFilter, number> => ({
  [AuditLogChangeFilters.added]: filterAuditLogsByChangeType({
    auditLogs,
    filters: [AuditLogChangeFilters.added],
  }).length,
  [AuditLogChangeFilters.removed]: filterAuditLogsByChangeType({
    auditLogs,
    filters: [AuditLogChangeFilters.removed],
  }).length,
  [AuditLogChangeFilters.changed]: filterAuditLogsByChangeType({
    auditLogs,
    filters: [AuditLogChangeFilters.changed],
  }).length,
  [AuditLogChangeFilters.reordered]: filterAuditLogsByChangeType({
    auditLogs,
    filters: [AuditLogChangeFilters.reordered],
  }).length,
});
